import React, {useRef} from 'react';
import PageTitle from '../../../../uiToolkit/PageTitle';
import './styles.css';
import {Alert} from 'react-bootstrap';
import {isEmail} from 'react-multi-email';
import {learnersText} from '../../learnersText';
import {getErrorFromCode} from '../../../../data/errorCodes';
import 'react-multi-email/dist/style.css';
import MultiInput from '../../../../uiToolkit/Inputs/MultiInput';
import BackButton from '../../../../uiToolkit/Buttons/BackButton';
import {useNavigate, useParams} from 'react-router-dom';

const ALLOWED_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M8 10V2.5M8 2.5L4.8 5.7M8 2.5l3.2 3.2M2.5 10.5v2a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FileChipIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M7 1H3.5A1 1 0 0 0 2.5 2v8a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V3.5L7 1z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M7 1v2.5h2.5" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

interface Props {
  onSubmit: (learners: string[]) => void;
  onCancel: () => void;
  onFileUpload: (file: File) => void;
  onPreviewSample: () => void;
  learners: string[];
  setLearners: (learners: string[]) => void;
  isLoading: boolean;
  failedInvitations: any;
}

const CreateLearnersUI = React.memo(
  ({
    onSubmit,
    onFileUpload,
    onPreviewSample,
    learners,
    setLearners,
    isLoading,
    failedInvitations,
  }: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const {organizationId} = useParams();

    const getFailedReasons = () => {
      if (failedInvitations) {
        return Object.values(failedInvitations).filter(
          (value, index, array) => {
            return array.indexOf(value) === index;
          },
        );
      }
      return [];
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        if (
          ALLOWED_FILE_TYPES.includes(file.type) ||
          file.name.endsWith('.csv') ||
          file.name.endsWith('.xlsx')
        ) {
          onFileUpload(file);
        } else {
          alert(learnersText.invalidFileFormat);
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    const handleDownloadTemplate = () => {
      const link = document.createElement('a');
      link.href = '/templates/learners-import-template.csv';
      link.download = 'learners-import-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const onBack = () => {
      navigate(`/manage/${organizationId}/learners`, {replace: true});
    };

    return (
      <div className="InviteLearnersPage">
        <BackButton overrideOnBack={onBack} />
        <PageTitle
          title={learnersText.inviteLearnersPageTitle}
          subtitle={learnersText.inviteLearnersPageSubtitle}
        />

        <div className="InviteEmailSection">
          <MultiInput
            validator={isEmail}
            value={learners}
            setValue={setLearners}
            heading={learnersText.HeadingInviteLearnerInputField}
            placeholder={learnersText.inputPlaceHolder}
          />
          <p className="InviteHint">{learnersText.emailSeparatorHint}</p>
          <button
            type="button"
            className="InviteSendButton"
            onClick={() => onSubmit(learners)}
            disabled={!learners.length || isLoading}>
            {learnersText.sendInvitation}
          </button>
        </div>

        <div className="InviteDivider" />

        <div className="InviteBulkSection">
          <p className="InviteBulkTitle">{learnersText.haveLongList}</p>
          <p className="InviteBulkText">
            {learnersText.importIntro}{' '}
            <span className="InviteLink" onClick={handleDownloadTemplate}>
              {learnersText.downloadTemplateLink}
            </span>{' '}
            {learnersText.downloadTemplateSuffix}
          </p>
          <div className="InviteBulkActions">
            <label
              className={`InviteUploadButton ${isLoading ? 'disabled' : ''}`}>
              <UploadIcon />
              {learnersText.uploadAFile}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileChange}
                className="HiddenFileInput"
                disabled={isLoading}
              />
            </label>
            <span className="InviteFileChip">
              <FileChipIcon />
              CSV
            </span>
            <span className="InviteFileChip">
              <FileChipIcon />
              XLSX
            </span>
          </div>
          <button
            type="button"
            className="InviteLink InvitePreviewSample"
            onClick={onPreviewSample}>
            {learnersText.previewSample}
          </button>
        </div>

        <div className="FailedEmailsContainer">
          {getFailedReasons().map((reason: any) => (
            <Alert key={reason.toString()} variant="warning">
              <p className="FailedEmailReason">{getErrorFromCode(reason)}</p>

              {Object.keys(failedInvitations)
                .filter(x => failedInvitations[x] === reason)
                .map(item => (
                  <p className="FailedEmailError" key={item}>
                    {item}
                  </p>
                ))}
            </Alert>
          ))}
        </div>
      </div>
    );
  },
);

export default CreateLearnersUI;
